'use client'

import {
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  Button
} from '@teqplay/ui'
import { useTranslations } from 'next-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useUser } from '@auth0/nextjs-auth0'
import { faSpinner } from '@fortawesome/pro-solid-svg-icons'
import { useCallback, useMemo, useRef, useEffect } from 'react'

import BookmarkCard from './BookmarkCard'
import { InfoTooltipContent } from '../ToolTipProvider/ToolTipProvider'
import VesselListCardLayout from '../VesselListCardLayout/VesselListCardLayout'
import GlobalPortSelection from '../Navbar/GlobalPortSelection'
import CardCTAPortReporter from './CardCTAPortReporter'

import useSwitchBookmarks from '@/app/hooks/useSwitchBookmarks'
import useVesselJourneyByPort from '@/app/hooks/teqplay-api/useVesselJourneyByPort'
import useVesselTabState, { TUseVesselTabStateOptions } from '@/app/hooks/useVesselTabState'
import useVesselScrollPosition from '@/app/hooks/useVesselScrollPosition'
import type {
  TVesselJourneyResponse,
  TGetVesselJourneyByPortInput
} from '@/api/v1/resources/vesselJourney'
import { getPortBySlug, TPortSlug } from '@/app/config/ports'
import { useActiveVessel } from '@/app/context/ActiveVesselContext'

import './BookmarkedVessels.scss'

type TProps = {
  vessel?: TVesselJourneyResponse
  disableRedirectTab?: boolean
  activeFirst?: boolean
  port?: TPortSlug
}

export default function BookmarkedVessels(props: TProps) {
  const t = useTranslations()
  const { user } = useUser()
  const selectedVessel = props.activeFirst
  const { setActiveVessel } = useActiveVessel()

  const explicitPortUnlocode = useMemo(() => {
    const portConfig = getPortBySlug(props.port ?? '')
    return portConfig?.unlocode
  }, [props.port])

  const tabStateOptions: TUseVesselTabStateOptions = {
    disableRedirectTab: props.disableRedirectTab,
    ...(explicitPortUnlocode ? { portUnlocode: explicitPortUnlocode } : {})
  }

  // Tab state management (two-level tabs)
  const {
    activeTopTab,
    activeSubTab,
    handleTopTabChange,
    handleSubTabChange,
    activeTab,
    portUnlocode,
    initializeTabForNewUser
  } = useVesselTabState(tabStateOptions)

  // Bookmarks data
  const {
    sortedBookmarks,
    bookmarks,
    loading: bookmarksLoading,
    handleRemoveBookmark,
    addBookmarkNow,
    getIsVesselBookmarked,
    isVesselBookmarkLoading
  } = useSwitchBookmarks()

  // Arriving vessels data
  const arrivingParams: TGetVesselJourneyByPortInput = useMemo(
    () => ({
      unlocode: portUnlocode,
      journeyStatus: 'EN_ROUTE'
    }),
    [portUnlocode]
  )
  const { sortedVessels: arrivingVessels, loading: arrivingLoading } =
    useVesselJourneyByPort(arrivingParams)

  // In-port vessels data
  const inPortParams: TGetVesselJourneyByPortInput = useMemo(
    () => ({
      unlocode: portUnlocode,
      journeyStatus: 'IN_ARRIVAL_PORT'
    }),
    [portUnlocode]
  )
  const {
    sortedVessels: inPortVessels,
    vessels: rawInPortVessels,
    loading: inPortLoading
  } = useVesselJourneyByPort(inPortParams)

  const shownArrivingVessels = useMemo(() => {
    const filteredVessels = (arrivingVessels ?? [])
      .filter(
        (vessel) =>
          !vessel.arrival?.estimatedTime ||
          new Date(vessel.arrival.estimatedTime).getTime() >= Date.now()
      )
      .sort((a, b) => {
        const etaA = a.arrival?.estimatedTime
        const etaB = b.arrival?.estimatedTime
        if (!etaA && !etaB) return 0
        if (!etaA) return 1
        if (!etaB) return -1
        return new Date(etaA).getTime() - new Date(etaB).getTime()
      })
    return filteredVessels
  }, [arrivingVessels])

  const shownInPortVessels = useMemo(() => {
    const sortedVessels = (inPortVessels ?? []).sort((a, b) => {
      const timeA = a.arrival?.actualTime ?? a.arrival?.estimatedTime
      const timeB = b.arrival?.actualTime ?? b.arrival?.estimatedTime
      if (!timeA && !timeB) return 0
      if (!timeA) return 1
      if (!timeB) return -1
      return new Date(timeA).getTime() - new Date(timeB).getTime()
    })
    return sortedVessels
  }, [inPortVessels])

  // Initialize tab for new users (set to arrival if no bookmarks)
  useEffect(() => {
    initializeTabForNewUser(!!(bookmarks && bookmarks.length > 0), bookmarksLoading)
  }, [bookmarks, bookmarksLoading, initializeTabForNewUser])

  // Calculate filtered arrival count (excluding vessels with past ETAs)
  const filteredArrivalCount = useMemo(() => {
    return (arrivingVessels ?? []).filter(
      (vessel) =>
        !vessel.arrival?.estimatedTime ||
        new Date(vessel.arrival.estimatedTime).getTime() >= Date.now()
    ).length
  }, [arrivingVessels])

  // Ref for the scrollable container
  const scrollContainerRef = useRef<HTMLUListElement>(null)

  // Track scroll position across tabs and handle vessel scrolling
  useVesselScrollPosition(activeTab, scrollContainerRef, props.vessel?.ship?.imo)

  // Build tabData object for easy access
  const tabData = useMemo(
    () => ({
      bookmarks: {
        vessels: sortedBookmarks,
        loading: bookmarksLoading,
        count: bookmarks?.length || 0
      },
      arrival: {
        vessels: shownArrivingVessels,
        loading: arrivingLoading,
        count: filteredArrivalCount
      },
      inPort: {
        vessels: shownInPortVessels,
        loading: inPortLoading,
        count: rawInPortVessels?.length || 0
      },
      // followed tab uses bookmarks data
      followed: {
        vessels: sortedBookmarks,
        loading: bookmarksLoading,
        count: bookmarks?.length || 0
      }
    }),
    [
      sortedBookmarks,
      bookmarksLoading,
      bookmarks?.length,
      shownArrivingVessels,
      arrivingLoading,
      filteredArrivalCount,
      shownInPortVessels,
      inPortLoading,
      rawInPortVessels?.length
    ]
  )

  // Set the first vessel in context when activeFirst is enabled
  useEffect(() => {
    if (!props.activeFirst) return

    const currentTabData =
      activeSubTab && activeSubTab in tabData ? tabData[activeSubTab as keyof typeof tabData] : null
    const firstVessel = currentTabData?.vessels?.[0] ?? tabData.arrival.vessels?.[0] ?? null

    setActiveVessel(firstVessel)
  }, [props.activeFirst, setActiveVessel, activeSubTab, tabData, activeTopTab])

  const focusToSearch = useCallback(() => {
    const searchInput: HTMLInputElement | null = document.querySelector('input[type="search"]')
    if (searchInput) {
      searchInput.focus()
    }
  }, [])

  const handleAddBookmarkOrSearch = useCallback(() => {
    if (props.vessel) {
      addBookmarkNow(props.vessel)
    } else {
      focusToSearch()
    }
  }, [props.vessel, addBookmarkNow, focusToSearch])

  const renderVesselList = useCallback(
    (vessels: TVesselJourneyResponse[] | undefined, isLoading: boolean, isFollowedTab = false) => {
      if (isLoading) {
        return (
          <div className="loading-bookmarks">
            <Spinner loading={true} size="l" />
          </div>
        )
      }

      if (!vessels || vessels.length === 0) {
        if (isFollowedTab) {
          return (
            <div className="empty-bookmarks">
              <p className="bodytext">{t('homePage.followEmptyTitle')}</p>
              <p className="subtext">{t('homePage.followEmptyDescription')}</p>
              <Button variant="brand-primary" onClick={handleAddBookmarkOrSearch}>
                {props.vessel ? t('homePage.addVesselToFleet') : t('homePage.followAddVessel')}
              </Button>
            </div>
          )
        } else {
          return (
            <div className="empty-bookmarks">
              <p className="bodytext">{t('homePage.noVessels')}</p>
            </div>
          )
        }
      }

      return (
        <ul className="bookmarks-list" ref={scrollContainerRef}>
          {vessels.map((vessel, i) => {
            if ('ship' in vessel) {
              return (
                <BookmarkCard
                  key={(vessel as TVesselJourneyResponse).ship.imo}
                  bookmark={vessel}
                  t={t}
                  user={user}
                  onRemoveBookmark={handleRemoveBookmark}
                  getIsVesselBookmarked={getIsVesselBookmarked}
                  addBookmarkNow={addBookmarkNow}
                  isBookmarkLoading={isVesselBookmarkLoading(vessel.ship.imo)}
                  active={
                    (i === 0 && selectedVessel) ||
                    props.vessel?.ship?.imo === (vessel as TVesselJourneyResponse).ship.imo
                  }
                />
              )
            } else {
              return null
            }
          })}
        </ul>
      )
    },
    [
      t,
      user,
      handleRemoveBookmark,
      props.vessel,
      handleAddBookmarkOrSearch,
      getIsVesselBookmarked,
      addBookmarkNow,
      isVesselBookmarkLoading,
      selectedVessel
    ]
  )

  // Memoize each vessel list to prevent unnecessary re-renders
  const bookmarksContent = useMemo(
    () => renderVesselList(tabData.bookmarks.vessels, tabData.bookmarks.loading, true),
    [renderVesselList, tabData.bookmarks.vessels, tabData.bookmarks.loading]
  )

  const arrivalContent = useMemo(() => {
    return renderVesselList(tabData.arrival.vessels, tabData.arrival.loading)
  }, [renderVesselList, tabData.arrival.vessels, tabData.arrival.loading])

  const inPortContent = useMemo(() => {
    return renderVesselList(tabData.inPort.vessels, tabData.inPort.loading)
  }, [renderVesselList, tabData.inPort.vessels, tabData.inPort.loading])

  return (
    <VesselListCardLayout className="bookmarks">
      <VesselListCardLayout.SearchField />

      {/* Top-level tabs */}
      <VesselListCardLayout.Tabs value={activeTopTab} onValueChange={handleTopTabChange}>
        <VesselListCardLayout.TabsList>
          <VesselListCardLayout.TabsTrigger value="port" className="main-page-port-tab">
            <VesselListCardLayout.TabTitle>
              <GlobalPortSelection />
            </VesselListCardLayout.TabTitle>
          </VesselListCardLayout.TabsTrigger>
          <VesselListCardLayout.TabsTrigger value="followed" className="main-page-follow-tab">
            <VesselListCardLayout.TabTitle>
              {tabData.bookmarks.loading && <FontAwesomeIcon icon={faSpinner} spin size="sm" />}
              <span>{t('Bookmarks.following')}</span>
              {tabData.bookmarks.count > 0 && (
                <span className="subtext">({tabData.bookmarks.count})</span>
              )}
            </VesselListCardLayout.TabTitle>
          </VesselListCardLayout.TabsTrigger>
        </VesselListCardLayout.TabsList>

        {/* Port tab content with sub-tabs */}
        <VesselListCardLayout.TabsContent value="port">
          <Tabs value={activeSubTab} onValueChange={handleSubTabChange}>
            <TabsList className="sub-tabs">
              <TabsTrigger value="arrival">
                <VesselListCardLayout.TabTitle>
                  {tabData.arrival.loading && <FontAwesomeIcon icon={faSpinner} spin size="sm" />}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{t('Bookmarks.arrival')}</span>
                      </TooltipTrigger>
                      <InfoTooltipContent messageKey="showUpcomingArrivals" />
                    </Tooltip>
                  </TooltipProvider>
                  {tabData.arrival.count > 0 && (
                    <span className="subtext">({tabData.arrival.count})</span>
                  )}
                </VesselListCardLayout.TabTitle>
              </TabsTrigger>
              <TabsTrigger value="inPort">
                <VesselListCardLayout.TabTitle>
                  {tabData.inPort.loading && <FontAwesomeIcon icon={faSpinner} spin size="sm" />}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{t('Bookmarks.inPort')}</span>
                      </TooltipTrigger>
                      <InfoTooltipContent messageKey="showVesselsInPort" />
                    </Tooltip>
                  </TooltipProvider>
                  {tabData.inPort.count > 0 && (
                    <span className="subtext">({tabData.inPort.count})</span>
                  )}
                </VesselListCardLayout.TabTitle>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="arrival">
              <CardCTAPortReporter />
              {arrivalContent}
            </TabsContent>
            <TabsContent value="inPort">
              <CardCTAPortReporter />
              {inPortContent}
            </TabsContent>
          </Tabs>
        </VesselListCardLayout.TabsContent>

        {/* Followed tab content */}
        <VesselListCardLayout.TabsContent value="followed">
          {bookmarksContent}
        </VesselListCardLayout.TabsContent>
      </VesselListCardLayout.Tabs>
    </VesselListCardLayout>
  )
}
